/**
 * IFB-163: keep Cognito bearer tokens out of remote logs.
 *
 * An Axios error carries `config.headers.Authorization`, and AxiosError.toJSON()
 * serialises `config`, so passing the raw error to the logger shipped the live
 * JWT to the Frontend-Logging CloudWatch group. These helpers are pure so they
 * can be unit tested without the MQTT or Pinia stores the logger depends on.
 */

export const REDACTED = '[REDACTED]'

const BEARER_RE = /\bbearer\s+[A-Za-z0-9._~+/=-]+/gi
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g
const MAX_DEPTH = 20

export interface CompactAxiosError {
  name: string
  message: string
  code?: string
  url?: string
  method?: string
  status?: number
}

/** Replace `Bearer <value>` credentials and JWT-shaped strings. */
export function redactText(text: string): string {
  return text.replace(BEARER_RE, `Bearer ${REDACTED}`).replace(JWT_RE, REDACTED)
}

export function isAxiosLikeError(value: unknown): value is Record<string, any> {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, any>
  return candidate.isAxiosError === true || candidate.name === 'AxiosError'
}

/** The fields worth logging from an Axios error, and nothing from `config.headers`. */
export function compactAxiosError(error: Record<string, any>): CompactAxiosError {
  const compact: CompactAxiosError = {
    name: String(error.name ?? 'AxiosError'),
    message: redactText(String(error.message ?? '')),
  }
  if (error.code !== undefined) compact.code = String(error.code)
  const url = error.config?.url
  if (typeof url === 'string') compact.url = redactText(url)
  const method = error.config?.method
  if (typeof method === 'string') compact.method = method
  const status = error.response?.status ?? error.status
  if (typeof status === 'number') compact.status = status
  return compact
}

const sanitizeValue = (value: unknown, seen: WeakSet<object>, depth: number): unknown => {
  if (isAxiosLikeError(value)) return compactAxiosError(value)
  if (typeof value === 'string') return redactText(value)
  if (!value || typeof value !== 'object') return value
  if (depth >= MAX_DEPTH || seen.has(value)) return value
  seen.add(value)

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, seen, depth + 1))
  }
  // Leave non-plain objects (Error, Date, Map, class instances) alone so the
  // existing serialisation behaviour for them is unchanged.
  const proto = Object.getPrototypeOf(value)
  if (proto !== Object.prototype && proto !== null) return value

  const out: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    out[key] = sanitizeValue(item, seen, depth + 1)
  }
  return out
}

/**
 * Prepare logger arguments for a remote sink (MQTT, Sentry breadcrumbs):
 * Axios errors are reduced to their safe fields and token-shaped strings are
 * redacted. Returns new values; the caller's arguments are not mutated.
 */
export function sanitizeLogArgs(args: unknown[]): unknown[] {
  const seen = new WeakSet<object>()
  return args.map((arg) => sanitizeValue(arg, seen, 0))
}
